import { observer } from "mobx-react";
import { useStore } from "@/stores/store-context";
import { useTranslation } from "@/i18n";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  PROFILE_SETTINGS_TABS,
  type TProfileSettingsTabKey,
} from "@/lib/permissions/profile-permissions";
import { ProfileGeneralPage } from "./panels/general";
import { ProfilePreferencesPage } from "./panels/preferences";
import { ProfileNotificationsPage } from "./panels/notifications";
import { ProfileSecurityPage } from "./panels/security";
import { ProfileWorkHoursPage } from "./panels/work-hours";
import { ProfileSkillsPage } from "./panels/skills";
import { ProfileConnectedAppsPage } from "./panels/connected-apps";

const TAB_PANELS: Record<TProfileSettingsTabKey, React.ComponentType> = {
  general: ProfileGeneralPage,
  preferences: ProfilePreferencesPage,
  notifications: ProfileNotificationsPage,
  security: ProfileSecurityPage,
  work_hours: ProfileWorkHoursPage,
  skills: ProfileSkillsPage,
  connected_apps: ProfileConnectedAppsPage,
};

export const ProfileSettingsModal = observer(function ProfileSettingsModal() {
  const { profileSettings } = useStore();
  const { t } = useTranslation();

  return (
    <Dialog open={profileSettings.isOpen} onOpenChange={(open) => profileSettings.setOpen(open)}>
      <DialogContent className="grid h-[min(85vh,720px)] w-full max-w-[calc(100vw-2rem)] grid-cols-1 gap-0 overflow-hidden p-0 text-sm sm:max-w-[960px] lg:grid-cols-[240px_minmax(0,1fr)]">
        <DialogTitle className="sr-only">{t("profile_settings.title")}</DialogTitle>
        <Tabs
          value={profileSettings.activeTab}
          onValueChange={(value) => profileSettings.setActiveTab(value as TProfileSettingsTabKey)}
          orientation="vertical"
          className="contents"
        >
          <aside className="overflow-y-auto border-border bg-muted/30 p-4 lg:border-r">
            <TabsList
              variant="line"
              className="h-auto w-full items-stretch gap-1 bg-transparent p-0"
            >
              {PROFILE_SETTINGS_TABS.map((tab) => (
                <TabsTrigger
                  key={tab.key}
                  value={tab.key}
                  className="justify-start px-3 py-2 text-sm"
                >
                  {t(tab.labelKey)}
                </TabsTrigger>
              ))}
            </TabsList>
          </aside>
          <div className="min-w-0 overflow-y-auto p-6">
            {PROFILE_SETTINGS_TABS.map((tab) => {
              const Panel = TAB_PANELS[tab.key];
              return (
                <TabsContent key={tab.key} value={tab.key}>
                  <Panel />
                </TabsContent>
              );
            })}
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
});
